import { useState } from 'react';
import { Info, Loader2, Pencil, RotateCcw } from 'lucide-react';
import type { Organization } from '../../types/admin';
import type { OrgSettingsUpdate } from '../../hooks/useOrgSettings';
import { ConfirmModal } from '../admin/ConfirmModal';

interface CalculateTaxesCardProps {
  org: Organization;
  onSave: (data: OrgSettingsUpdate) => Promise<unknown>;
  onRestore?: () => Promise<void>;
}

/** Display a decimal rate as a user-friendly percentage string (e.g. 0.05 → "5%"). */
function formatRate(rate: number | undefined): string {
  if (rate === undefined || rate === 0) return '0%';
  // Multiply by 100, round to avoid floating-point artefacts, then format.
  const pct = Math.round(rate * 10000) / 100;
  return `${pct}%`;
}

export function CalculateTaxesCard({ org, onSave, onRestore }: CalculateTaxesCardProps) {
  const autoEnabled = org.calculateTaxes ?? false;
  const savedRate = org.defaultTaxRate ?? 0;

  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showRestoreModal, setShowRestoreModal] = useState(false);

  // Local draft state (only used while editing)
  const [draftAutoEnabled, setDraftAutoEnabled] = useState(autoEnabled);
  const [draftRateInput, setDraftRateInput] = useState(formatRate(savedRate));

  function startEdit() {
    setDraftAutoEnabled(autoEnabled);
    setDraftRateInput(formatRate(savedRate));
    setError('');
    setEditing(true);
  }

  function cancelEdit() {
    setEditing(false);
    setError('');
  }

  async function handleSave() {
    setSaving(true);
    setError('');
    try {
      const updates: OrgSettingsUpdate = { calculateTaxes: draftAutoEnabled };

      if (!draftAutoEnabled) {
        // Parse percentage input → decimal (e.g. "5" or "5%" → 0.05)
        const cleaned = draftRateInput.replace('%', '').trim();
        const pct = parseFloat(cleaned);
        if (isNaN(pct) || pct < 0 || pct > 100) {
          setError('Enter a valid percentage between 0 and 100.');
          setSaving(false);
          return;
        }
        updates.defaultTaxRate = Math.round(pct * 100) / 10000; // e.g. 5 → 0.05
      }

      await onSave(updates);
      setEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    }
    setSaving(false);
  }

  return (
    <section className="bg-white border border-stone rounded-xl shadow-brand-sm overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3.5 border-b border-stone">
        <div className="flex items-center justify-between gap-3">
          <div className="min-w-0">
            <p className="font-semibold text-small text-midnight">Tax Settings</p>
            <p className="text-[12px] text-slate leading-snug mt-0.5">
              Configure how taxes are applied to quotes.
            </p>
          </div>
          <div className="flex items-center gap-2">
          {onRestore && !editing && (
            <button
              type="button"
              onClick={() => setShowRestoreModal(true)}
              className="flex items-center gap-1 px-2 py-1 text-[11px] font-medium text-slate hover:text-plum transition-colors"
              title="Restore from platform defaults"
            >
              <RotateCcw size={11} />
              <span className="hidden sm:inline">Restore</span>
            </button>
          )}
          {!editing ? (
            <button
              type="button"
              onClick={startEdit}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-plum border border-plum rounded-lg hover:bg-plum/5 transition-colors"
            >
              <Pencil size={12} />
              Edit
            </button>
          ) : (
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSave}
                disabled={saving}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-plum text-white rounded-lg hover:bg-plum/90 disabled:opacity-50 transition-colors"
              >
                {saving && <Loader2 size={12} className="animate-spin" />}
                Save
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                disabled={saving}
                className="px-3 py-1.5 text-xs font-medium text-slate border border-stone rounded-lg hover:bg-mist transition-colors"
              >
                Cancel
              </button>
            </div>
          )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-3.5">
        {error && (
          <div className="mb-3 px-3 py-2 rounded-lg bg-rose/10 border border-rose/30 text-[12px] text-rose">
            {error}
          </div>
        )}

        {/* Automatically Calculate Taxes toggle */}
        <div className="flex items-center justify-between gap-3 py-3 border-b border-stone">
          <div className="min-w-0">
            <p className="text-[13px] font-semibold text-midnight">Automatically Calculate Taxes</p>
            <p className="text-[12px] text-slate leading-snug mt-0.5">
              Look up sales tax rates by job location using zip.tax.
            </p>
          </div>
          {editing ? (
            <button
              type="button"
              role="switch"
              aria-checked={draftAutoEnabled}
              disabled={saving}
              onClick={() => setDraftAutoEnabled(!draftAutoEnabled)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent
                          transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-plum/20
                          ${draftAutoEnabled ? 'bg-plum' : 'bg-stone'}
                          ${saving ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0
                            transition duration-200 ease-in-out ${draftAutoEnabled ? 'translate-x-5' : 'translate-x-0'}`}
              />
            </button>
          ) : (
            <span className={`text-[13px] font-semibold ${autoEnabled ? 'text-plum' : 'text-slate'}`}>
              {autoEnabled ? 'On' : 'Off'}
            </span>
          )}
        </div>

        {/* Default Tax Rate — only shown when auto is OFF */}
        {(editing ? !draftAutoEnabled : !autoEnabled) && (
          <div className="flex items-center justify-between gap-3 py-3 border-b border-stone">
            <div className="min-w-0">
              <p className="text-[13px] font-semibold text-midnight">Default Tax Rate</p>
              <p className="text-[12px] text-slate leading-snug mt-0.5">
                Flat rate applied to all quotes when automatic calculation is off.
              </p>
            </div>
            {editing ? (
              <div className="relative w-24 shrink-0">
                <input
                  type="text"
                  inputMode="decimal"
                  value={draftRateInput}
                  onChange={(e) => setDraftRateInput(e.target.value)}
                  disabled={saving}
                  placeholder="0%"
                  className="w-full px-3 py-1.5 pr-7 text-[13px] text-right font-semibold text-midnight
                             border border-stone rounded-lg focus:outline-none focus:ring-2 focus:ring-plum/20 focus:border-plum
                             disabled:opacity-50"
                />
                <span className="absolute right-3 top-1/2 -translate-y-1/2 text-[13px] text-slate pointer-events-none">%</span>
              </div>
            ) : (
              <span className="text-[13px] font-semibold text-midnight shrink-0">
                {formatRate(savedRate)}
              </span>
            )}
          </div>
        )}

        {/* Callout */}
        <div className="mt-3.5">
          {(editing ? draftAutoEnabled : autoEnabled) ? (
            <div className="flex items-start gap-3 px-3.5 py-3 rounded-xl bg-amber-50 border border-amber-200">
              <Info size={16} className="shrink-0 text-amber-600 mt-0.5" />
              <p className="text-[13px] text-amber-800 leading-relaxed">
                Automatic tax calculation is enabled. IOMI Pro will calculate estimated sales tax based on the
                job&apos;s origin address.{' '}
                <strong>
                  Your company is ultimately responsible for ensuring the correct taxes are collected
                  for your jurisdiction.
                </strong>{' '}
                Consult your tax professional.
              </p>
            </div>
          ) : (
            <div className="flex items-start gap-3 px-3.5 py-3 rounded-xl bg-mist border border-stone">
              <Info size={16} className="shrink-0 text-slate mt-0.5" />
              <p className="text-[13px] text-slate leading-relaxed">
                Automatic tax calculation is disabled.{' '}
                <strong>
                  Your company is responsible for calculating and collecting any applicable taxes
                  independently.
                </strong>
              </p>
            </div>
          )}
        </div>
      </div>
      {onRestore && (
        <ConfirmModal
          isOpen={showRestoreModal}
          title="Restore defaults"
          message="This will override your current tax settings with the platform defaults. This action cannot be undone."
          confirmLabel="Restore"
          variant="danger"
          onConfirm={() => onRestore()}
          onClose={() => setShowRestoreModal(false)}
        />
      )}
    </section>
  );
}
